<template>
  <div class="ds-page container-fluid p-0">
    <div class="row no-gutters ds-layout">
      <!-- Left: Preview -->
      <div class="col-lg-8 col-xl-9">
        <div
          v-if="isFreeLookActive"
          class="sticky-top"
          :style="{ top: navHeight, zIndex: 11 }"
        >
          <div
            class="position-absolute frosted px-3 py-1 mt-4 small"
            style="left: 50%; transform: translateX(-50%);"
          >
            Press <kbd>Esc</kbd> to exit free-look
          </div>
        </div>
        <div class="position-relative">
          <FrameRenderer
            ref="frameRenderer"
            :art-width="renderArtWidth"
            :art-height="renderArtHeight"
            :target-width="resolvedFinishedDimensions.width"
            :target-height="resolvedFinishedDimensions.height"
            :artwork-image-url="artworkImageUrl"
            :artwork-scale="artworkScale"
            :artwork-offset-left="artworkOffsetLeft"
            :artwork-offset-top="artworkOffsetTop"
            :debug-checker="isDebug"
            :substrate="frameConfig ? frameConfig.substrate : currentSubstrateName"
            :frame-data="frameConfig ? frameConfig.frameData : null"
            :mat-data="frameConfig ? frameConfig.matData : null"
            :mat-data-array="frameConfig ? frameConfig.matDataArray : null"
            :mat-width="frameConfig ? frameConfig.matWidth : { top: 0, bottom: 0, left: 0, right: 0 }"
            :mat-widths-array="frameConfig ? frameConfig.matWidthsArray : null"
            :hang-data="frameConfig ? frameConfig.hangData : null"
            :stretcher-depth="frameConfig ? frameConfig.stretcherDepth : null"
            :canvas-mount="frameConfig ? frameConfig.canvasMount : null"
            :float-frame-data="frameConfig ? frameConfig.floatFrameData : null"
            :substrate-back-data="frameConfig ? frameConfig.substrateBackData : null"
            :thumbnail-url="thumbnailUrl"
            :thumbnail-transition-name="thumbnailTransitionName"
            :awaiting-config="isInitialConfigLoading"
            :canvas-enabled="canRenderFrameRenderer && !!availableOptions?.length"
            :hide-canvas="designStudioTransitionActive"
            :wall-enabled="true"
            :camera-mode="cameraMode"
            :decor-enabled="true"
            max-width="100%"
            :max-height="`calc(100vh - ${navHeight} - 48px)`"
            :interactive="true"
            @resize="onFrameResize"
            @source-transform="onSourceTransform"
            @transition-thumbnail-ready="onTransitionThumbnailReady"
            @request-camera-mode="onRequestCameraMode"
            @enter-free-look="onEnterFreeLook"
            @exit-free-look="onExitFreeLook"
          />
          <div class="position-absolute d-flex gap-2" style="bottom: 2rem; left: 50%; transform: translateX(-50%); z-index: 10;">
            <b-button-group size="sm" class="frosted">
              <b-button
                :variant="cameraMode === 'standard' ? 'secondary' : 'light'"
                @click="setCameraMode('standard')"
                title="Standard scale"
              >
                Standard
              </b-button>
              <b-button
                :variant="cameraMode === 'wall' ? 'secondary' : 'light'"
                @click="setCameraMode('wall')"
                title="Show full wall"
              >
                Wall
              </b-button>
              <b-button
                :variant="cameraMode === CAMERA_MODE_FREE ? 'secondary' : 'light'"
                @click="setCameraMode(CAMERA_MODE_FREE)"
                title="Free look mode (WASD + mouse)"
              >
                Free
              </b-button>
            </b-button-group>
            <b-button
              size="sm"
              variant="light"
              @click="toggleFrameFlip"
              title="Flip to see back and hang hardware"
            >
              Flip View
            </b-button>
            <b-button
              size="sm"
              variant="light"
              @click="downloadRender"
              title="Download as PNG"
            >
              Download Image
            </b-button>
          </div>
        </div>
      </div>

      <!-- Right: Options Sidebar -->
      <div class="col-lg-4 col-xl-3 px-3 ds-sidebar mb-chat">
        <h4 class="text-center my-3">Design Studio</h4>
        <DesignStudioItemForm
          :substrates="substrates"
          :selected-substrate.sync="selectedSubstrate"
          :available-options="availableOptions"
          :selected-options.sync="selectedOptions"
          :selected-subcategories.sync="selectedSubcategories"
          :dimensions.sync="dimensions"
          :artwork-scale.sync="artworkScale"
          :artwork-offset-left.sync="artworkOffsetLeft"
          :artwork-offset-top.sync="artworkOffsetTop"
          :workspace.sync="selectedWorkspace"
          :quantity.sync="quantity"
          :location.sync="location"
          :price-data="priceData"
          :pricing-enabled="isDebug"
          :selected-image="selectedImage"
          :image-id-error="imageIdError"
          :profile="profile"
          :loading-substrates="loadingSubstrates"
          :loading-options="loadingOptions"
          :validation-errors="validationErrors"
          :show-workspace="true"
          @frame-config-changed="onFrameConfigChanged"
          @image-id-change="onImageIdChange"
        />
        <div
          v-if="selectedSubstrate"
          class="sticky-bottom frosted eat-gutter py-3 bottom-bar"
        >
          <div class="d-flex align-items-center justify-content-end">
            <div v-if="errorCount" class="mr-3 d-flex align-items-center">
              <b-popover
                target="design-studio-errors"
                triggers="hover click blur"
                placement="top"
              >
                <ul class="mb-0 pl-3" style="max-width: 400px;">
                  <li v-for="(msg, key) in validationErrors" :key="key">
                    <strong>{{ key }}:</strong> {{ msg }}
                  </li>
                </ul>
              </b-popover>
              <span
                id="design-studio-errors"
                class="text-danger d-flex align-items-center"
                style="cursor: pointer;"
              >
                <IconX width="1em" height="1em" viewBox="0 0 16 16" class="fill-color mr-1" />
                {{ errorCount }} {{ errorCount === 1 ? 'error' : 'errors' }}
              </span>
            </div>

            <div v-if="priceData" class="d-flex align-items-center gap-3 mr-3">
              <b-form-checkbox
                v-if="isStaff"
                :checked="isDebug"
                @change="onDebugToggle"
                size="sm"
                class="mb-0 ds-debug-toggle"
              >
                Debug
              </b-form-checkbox>
              <IconCheck
                v-if="priceCommitted"
                width="1em"
                height="1em"
                class="fill-color text-success"
              />
              <span
                id="design-studio-price"
                :class="{'text-muted': pricingLoading}"
              >
                ${{ formatPrice(priceData.total_sell_price || priceData.discounted_sell_price || priceData.clean_sell_price || '—') }}
              </span>
            </div>

            <LoadingButton
              variant="primary"
              @click="saveDesign"
              :busy="saving"
              :disabled="saving"
            >
              Save
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isDebug && selectedSubstrate" class="container-fluid pt-3 pb-chat border-top">
      <div class="row">
        <div class="col-lg-6">
          <h4 class="py-2 frosted sticky-top" :style="{top: navHeight}">request payload</h4>
          <pre class="bg-light p-3 whitespace-pre-wrap" style="overflow-wrap: anywhere;">{{ JSON.stringify(itemData, null, 2) }}</pre>
        </div>
        <div class="col-lg-6">
          <h4 class="py-2 frosted sticky-top" :style="{top: navHeight}">response.data.price_data</h4>
          <pre class="bg-light p-3 whitespace-pre-wrap" style="overflow-wrap: anywhere;">{{ JSON.stringify(priceData, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { http, http2, getAppApi } from '@/services/http.js'
import { groupBy, debounce } from 'lodash'
import LoadingButton from '@/components/LoadingButton.vue'
import FrameRenderer from '@/components/FrameRenderer.vue'
import DesignStudioItemForm from '@/components/DesignStudioItemForm.vue'
import { mapState } from 'vuex'
import IconCheck from '@primer/octicons/build/svg/check-16.svg'
import IconX from '@primer/octicons/build/svg/x-circle-fill-16.svg'
import { CAMERA_MODE_FREE, shouldPromoteCameraMode } from '@/utils/designStudioScale.js'
import { computeOuterDimensions } from '@/utils/frameGeometry.js'
import { getSrc } from '@/services/media.js'

const RETURN_ITEM_STORAGE_KEY = 'projects2-vt-return-item'

export default {
  name: 'DesignStudio',
  components: {
    LoadingButton,
    FrameRenderer,
    DesignStudioItemForm,
    IconCheck,
    IconX,
  },
  data() {
    return {
      CAMERA_MODE_FREE,
      substrates: [],
      selectedSubstrate: null,
      availableOptions: [],
      selectedOptions: {},
      selectedSubcategories: {}, // Store selected subcategory for each category
      selectedImage: null,
      selectedWorkspace: null,
      quantity: 1,
      location: '',
      dimensions: {
        width: '',
        height: ''
      },
      // Source image transform: scale (1 = cover-fit) + offset from center (inches)
      artworkScale: 1,
      artworkOffsetLeft: 0,
      artworkOffsetTop: 0,
      finishedDimensions: {
        width: null,
        height: null,
      },
      loadingSubstrates: false,
      loadingOptions: false,
      saving: false,
      pendingChosenOptions: null,
      priceData: null,
      priceCommitted: false,
      pricingLoading: false,
      imageIdError: '',
      loadingItem: false,
      validationErrors: {},
      frameImageKeys: ['front_image', 'side_image', 'top_image'],
      frameConfig: null,
      thumbnailUrl: null,
      canRenderFrameRenderer: false,
      cameraMode: null,
      isFreeLookActive: false,
    }
  },

  mounted() {
    document.addEventListener('keydown', this.onGlobalKeyDown)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onGlobalKeyDown)
  },
  computed: {
    isDebug() {
      const value = this.$route?.query?.debug
      if (value === true) return true
      const normalized = String(value || '').trim().toLowerCase()
      return ['1', 'true', 'yes', 'on'].includes(normalized)
    },
    isStaff() {
      return this.$store.getters['user/isStaff']
    },
    errorCount() {
      return Object.keys(this.validationErrors).length
    },
    substrateOptions() {
      return this.substrates.map(s => ({
        value: s.id,
        text: s.name
      }))
    },
    groupedOptions() {
      // Group by category name (field 'name' in serializer)
      return groupBy(this.availableOptions, 'name')
    },
    isInitialConfigLoading() {
      // Determines if the initial load of a saved design is still fetching its API dependencies
      return this.loadingItem || this.loadingOptions || this.pendingChosenOptions !== null
    },
    renderArtWidth() {
      if (this.dimensions.width) return this.dimensions.width
      return 16
    },
    renderArtHeight() {
      if (this.dimensions.height) return this.dimensions.height
      return 20
    },
    currentSubstrateName() {
      const sub = this.substrates.find(s => s.id === this.selectedSubstrate)
      return sub ? sub.name : ''
    },
    itemData() {
      if (!this.selectedSubstrate) return {}

      const chosenOptions = []
      const isMiscCategory = category => String(category || '').trim().toLowerCase() === 'misc'
      const substrateName = String(this.currentSubstrateName || '').trim().toLowerCase()

      Object.entries(this.selectedOptions).forEach(([category, optionId]) => {
        if (isMiscCategory(category)) return
        if (!optionId) return

        // Find the option object in availableOptions
        const option = this.availableOptions.find(o => o.id === optionId)
        if (option) {
          chosenOptions.push({
            option_id: optionId,
            name: category,
            value: option.value !== null && option.value !== undefined ? option.value : option.name
          })
        }
      })

      const autoMiscOptions = this.availableOptions.filter(opt => {
        if (!isMiscCategory(opt?.name)) return false
        const optSubstrate = String(opt?.substrate || '').trim().toLowerCase()
        return optSubstrate && optSubstrate === substrateName
      })

      autoMiscOptions.forEach(option => {
        chosenOptions.push({
          option_id: option.id,
          name: option.name,
          value: option.value !== null && option.value !== undefined ? option.value : option.name,
        })
      })

      const config = {
        imagedata: this.selectedImage,
        // Top-level width/height are finished dimensions.
        width: this.finishedSizePayload.w,
        height: this.finishedSizePayload.h,
        // API expects workspace as a string name and project_id as int
        project_id: this.selectedWorkspace?.project || this.profile?.current_project?.id || null,
        workspace: this.selectedWorkspace?.name || this.selectedWorkspace?.label || '',
        quantity: Number(this.quantity) || 1,
        location: this.location || '',
        config_data: {
          substrate: this.currentSubstrateName,
          width: this.dimensions.width,
          height: this.dimensions.height,
          chosen_options: chosenOptions
        }
      }

      if (this.artworkScale !== 1 || this.artworkOffsetLeft !== 0 || this.artworkOffsetTop !== 0) {
        config.config_data.artwork_scale = this.artworkScale
        config.config_data.artwork_offset_left = this.artworkOffsetLeft
        config.config_data.artwork_offset_top = this.artworkOffsetTop
      }

      return config
    },
    finishedSizePayload() {
      const artW = parseFloat(this.renderArtWidth) || 16
      const artH = parseFloat(this.renderArtHeight) || 20
      const cfg = this.frameConfig || {}

      const substrateName = String(cfg.substrate || this.currentSubstrateName || '').toLowerCase()
      const isCanvas = substrateName === 'canvas'
      const canvasMount = String(cfg.canvasMount || '').trim()
      const isGalleryWrapped = isCanvas && /gallery\s*wrapped/i.test(canvasMount)
      const isFloatFrame = isCanvas && /float\s*frame/i.test(canvasMount)

      const mwRaw = cfg.matWidth && typeof cfg.matWidth === 'object'
        ? cfg.matWidth
        : { top: 0, bottom: 0, left: 0, right: 0 }
      const matWidth = isGalleryWrapped
        ? { top: 0, bottom: 0, left: 0, right: 0 }
        : {
          top: parseFloat(mwRaw.top) || 0,
          bottom: parseFloat(mwRaw.bottom) || 0,
          left: parseFloat(mwRaw.left) || 0,
          right: parseFloat(mwRaw.right) || 0,
        }

      const effectiveFrameData = isFloatFrame && cfg.floatFrameData ? cfg.floatFrameData : cfg.frameData
      const baseFrameWidth = effectiveFrameData ? (parseFloat(effectiveFrameData.width) || 0) : 0
      const effectiveFrameWidth = isFloatFrame && cfg.floatFrameData
        ? (parseFloat(cfg.floatFrameData.width) || baseFrameWidth)
        : baseFrameWidth
      const showFrame = !isGalleryWrapped && !!effectiveFrameData && effectiveFrameWidth > 0

      const floatFrameGap = isFloatFrame ? (parseFloat(cfg.floatFrameData?.gap) || 0.25) : 0
      const artWidthForOuter = isFloatFrame ? artW + (2 * floatFrameGap) : artW
      const artHeightForOuter = isFloatFrame ? artH + (2 * floatFrameGap) : artH

      const substrateBorder = parseFloat(cfg.substrateBackData?.border) || 0
      const rabbet = effectiveFrameData && effectiveFrameData.rabbet != null
        ? (parseFloat(effectiveFrameData.rabbet) || 0)
        : 0

      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: artWidthForOuter,
        artHeight: artHeightForOuter,
        matTop: matWidth.top,
        matBottom: matWidth.bottom,
        matLeft: matWidth.left,
        matRight: matWidth.right,
        frameWidth: showFrame ? effectiveFrameWidth : 0,
        rabbet,
        substrateBorder,
      })

      return {
        w: Number((parseFloat(outerWidth) || artW).toFixed(2)),
        h: Number((parseFloat(outerHeight) || artH).toFixed(2)),
      }
    },
    resolvedFinishedDimensions() {
      return {
        width: this.finishedDimensions?.width || this.finishedSizePayload.w,
        height: this.finishedDimensions?.height || this.finishedSizePayload.h,
      }
    },
    /**
     * Best available image URL for the artwork preview.
     */
    artworkImageUrl() {
      if (!this.selectedImage) return null
      return getSrc(this.selectedImage) || null
    },
    thumbnailTransitionName() {
      const itemId = this.$route?.query?.item
      if (!itemId) return ''
      return 'ds-item-active'
    },
    designStudioTransitionActive() {
      const routeTransition = this.$store.state.appearance?.routeTransition
      return routeTransition === 'design-studio-item'
    },
    navHeight() {
      return this.$store.state.appearance.navHeight
    },
    ...mapState('user', ['profile'])
  },
  watch: {
    selectedSubstrate(newVal) {
      this.selectedOptions = {}
      this.selectedSubcategories = {}
      if (newVal) {
        this.fetchOptions(newVal)
      } else {
        this.availableOptions = []
      }
      if (!this.loadingItem) {
        this.debouncedUpdatePriceData()
      }
    },
    '$route.query': {
      handler(query, oldQuery) {
        const oldItemId = oldQuery?.item
        const oldImageId = oldQuery?.image_id
        const itemId = query?.item
        const imageId = query?.image_id

        const itemUnchanged = oldQuery !== undefined && String(oldItemId || '') === String(itemId || '')
        const imageUnchanged = oldQuery !== undefined && String(oldImageId || '') === String(imageId || '')
        if (itemUnchanged && imageUnchanged) {
          this.debouncedUpdatePriceData()
          return
        }

        if (itemId) {
          // For existing items, load from API (including any stored price_data)
          // and treat that as committed price until the user makes changes.
          this.loadItem(itemId)
        } else if (imageId) {
          this.loadImageFromQuery(imageId, query)
        } else {
          // For new items, compute preview pricing as the user configures options.
          this.debouncedUpdatePriceData()
        }
      },
      immediate: true,
    },
    availableOptions: {
      handler(opts) {
        if (opts.length && this.pendingChosenOptions?.length) {
          const selected = {}
          this.pendingChosenOptions.forEach(co => {
            const opt = opts.find(o =>
              o.id === co.option_id || (o.name === co.name && String(o.value || o.name) === String(co.value))
            )
            if (opt) selected[opt.name] = opt.id
          })
          this.selectedOptions = { ...this.selectedOptions, ...selected }
          this.pendingChosenOptions = null
        }
      },
    },
    selectedOptions: {
      handler(newVal, oldVal) {
        if (!this.loadingItem) {
          this.debouncedUpdatePriceData()
          this.applyDefaultOptions(newVal, oldVal)
        }
        this.syncSubcategories(newVal)
      },
      deep: true,
    },
    dimensions: {
      handler() {
        if (!this.loadingItem) {
          this.debouncedUpdatePriceData()
        }
      },
      deep: true,
    },
    selectedWorkspace() {
      if (!this.loadingItem) {
        this.debouncedUpdatePriceData()
      }
    },
    quantity() {
      if (!this.loadingItem) {
        this.debouncedUpdatePriceData()
      }
    },
    selectedImage() {
      this.imageIdError = ''
      if (!this.loadingItem) {
        this.debouncedUpdatePriceData()
        this.ensureSourceCropDefaults(true)
      }
    },
    designStudioTransitionActive: {
      immediate: true,
      handler(isActive) {
        if (!isActive) {
          this.canRenderFrameRenderer = true
        }
      },
    },
  },
  created() {
    this.debouncedUpdatePriceData = debounce(this.updatePriceData, 200)
    this.fetchSubstrates()
  },
  beforeRouteLeave(to, from, next) {
    if (to.name === 'projects2-detail') {
      const itemId = this.$route?.query?.item
      if (itemId) {
        try {
          sessionStorage.setItem(RETURN_ITEM_STORAGE_KEY, String(itemId))
        } catch (error) {
          // ignore session storage failures
        }
      }
    }
    next()
  },
  methods: {
    /**
     * Derive the natural aspect ratio of the selected image if metadata is available.
     * Falls back to the current artwork aspect if image dimensions are unknown.
     */
    getSelectedImageAspect() {
      const img = this.selectedImage || {}
      const candidates = [
        [img.width, img.height],
        [img.pixel_width, img.pixel_height],
        [img.pixelWidth, img.pixelHeight],
        [img.w, img.h],
      ]
      for (const [w, h] of candidates) {
        const nw = Number(w)
        const nh = Number(h)
        if (Number.isFinite(nw) && Number.isFinite(nh) && nw > 0 && nh > 0) {
          return nw / nh
        }
      }
      const artW = parseFloat(this.dimensions.width || this.renderArtWidth) || 16
      const artH = parseFloat(this.dimensions.height || this.renderArtHeight) || 20
      return artW / artH
    },

    ensureSourceCropDefaults(force = false) {
      if (force) {
        this.artworkScale = 1
        this.artworkOffsetLeft = 0
        this.artworkOffsetTop = 0
      }
    },

    clampSourceCropToBounds() {
      const artW = parseFloat(this.dimensions.width || this.renderArtWidth)
      const artH = parseFloat(this.dimensions.height || this.renderArtHeight)
      if (!Number.isFinite(artW) || !Number.isFinite(artH) || artW <= 0 || artH <= 0) return

      const imgAspect = this.getSelectedImageAspect()
      if (!Number.isFinite(imgAspect) || imgAspect <= 0) return

      const s = Math.max(1, this.artworkScale)
      const baseCoverW = Math.max(artW, artH * imgAspect)
      const baseCoverH = baseCoverW / imgAspect
      const uw = baseCoverW * s
      const uh = baseCoverH * s

      const maxOffsetLeft = (uw - artW) / 2
      const maxOffsetTop = (uh - artH) / 2

      this.artworkScale = Math.round(s * 100) / 100
      const clampedLeft = Math.min(Math.max(-maxOffsetLeft, this.artworkOffsetLeft), maxOffsetLeft)
      const clampedTop = Math.min(Math.max(-maxOffsetTop, this.artworkOffsetTop), maxOffsetTop)
      this.artworkOffsetLeft = Math.round(clampedLeft * 100) / 100
      this.artworkOffsetTop = Math.round(clampedTop * 100) / 100
    },
    onDebugToggle(enabled) {
      const shouldEnable = !!enabled
      if (shouldEnable === this.isDebug) {
        return
      }

      const query = { ...this.$route.query }
      if (shouldEnable) {
        query.debug = '1'
      } else {
        delete query.debug
      }

      this.$router.replace({
        path: this.$route.path,
        query,
      })
    },
    async onImageIdChange(imageId) {
      const normalizedId = String(imageId || '').trim()

      if (!normalizedId) {
        this.imageIdError = ''
        this.selectedImage = null
        this.thumbnailUrl = null
        return
      }

      try {
        const response = await http.get(`content/retrieve/${encodeURIComponent(normalizedId)}/`)
        const image = response?.data
        if (!image?.id) {
          throw new Error('Image not found')
        }
        this.selectedImage = image
        this.imageIdError = ''
      } catch (e) {
        this.imageIdError = 'Invalid image id'
      }
    },
    onFrameConfigChanged(config) {
      this.frameConfig = config
    },
    syncSubcategories(selectedOpts) {
      if (!selectedOpts || !this.availableOptions.length) return
      const grouped = this.groupedOptions
      const updated = { ...this.selectedSubcategories }
      let changed = false

      Object.entries(grouped).forEach(([category, options]) => {
        // Only for categories that have subcategories
        const hasSubs = options.some(o => o.extra_data && Array.isArray(o.extra_data.color_groups) && o.extra_data.color_groups.length)
        if (!hasSubs) return

        const selectedId = selectedOpts[category]
        if (!selectedId) return

        const selectedOpt = options.find(o => o.id === selectedId)
        if (!selectedOpt) return

        const groups = selectedOpt.extra_data && selectedOpt.extra_data.color_groups
        if (Array.isArray(groups) && groups.length) {
          // If the current subcategory filter doesn't include the selected option, update it
          const currentSub = this.selectedSubcategories[category]
          if (!currentSub || !groups.includes(currentSub)) {
            updated[category] = groups[0]
            changed = true
          }
        }
      })

      if (changed) {
        this.selectedSubcategories = updated
      }
    },
    applyDefaultOptions(newVal, oldVal) {
      if (!newVal || !this.availableOptions.length) return
      // Find which categories changed
      const changes = {}
      Object.keys(newVal).forEach(cat => {
        if (!oldVal || newVal[cat] !== oldVal[cat]) {
          changes[cat] = newVal[cat]
        }
      })
      if (!Object.keys(changes).length) return

      // For each changed option, look up its default_options and apply them
      const updates = {}
      Object.values(changes).forEach(optionId => {
        if (!optionId) return
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (!opt || !opt.default_options || !opt.default_options.length) return
        opt.default_options.forEach(def => {
          // def has { id, category_id, category }
          // Only apply if the user hasn't already selected something in that category,
          // or if the current selection was also a default (don't override manual picks)
          const cat = def.category
          if (cat && def.id && !updates[cat]) {
            const existingSelection = newVal?.[cat]
            if (existingSelection != null && existingSelection !== '') return
            // Verify the default option exists in our available options
            const exists = this.availableOptions.find(o => o.id === def.id)
            if (exists) {
              updates[cat] = def.id
            }
          }
        })
      })

      if (Object.keys(updates).length) {
        this.selectedOptions = { ...this.selectedOptions, ...updates }
      }
    },
    formatPrice(val) {
      const n = parseFloat(val)
      return isNaN(n) ? val : n.toFixed(2)
    },
    parseValidationErrors(e) {
      const data = e && e.response && e.response.data
      if (!data || typeof data !== 'object') return {}
      // Flatten nested errors like { config_data: { width: [...] } }
      // into { 'config_data.width': 'message' }
      const errors = {}
      const chosenOptions = this.itemData?.config_data?.chosen_options || []
      const flatten = (obj, prefix) => {
        Object.entries(obj).forEach(([key, val]) => {
          const path = prefix ? `${prefix}.${key}` : key
          if (Array.isArray(val)) {
            // Array of strings = simple error list
            if (val.length && typeof val[0] === 'string') {
              errors[path] = val.join(', ')
            } else {
              // Array of objects (e.g. chosen_options per-index errors)
              val.forEach((item, idx) => {
                if (item && typeof item === 'object' && Object.keys(item).length) {
                  // Try to use the option name for a friendlier key
                  const optionName = chosenOptions[idx]?.name || `#${idx}`
                  const label = prefix ? `${prefix}.${optionName}` : `${key}.${optionName}`
                  flatten(item, label)
                }
              })
            }
          } else if (val && typeof val === 'object') {
            flatten(val, path)
          } else if (typeof val === 'string') {
            errors[path] = val
          }
        })
      }
      flatten(data, '')
      return errors
    },
    getFieldError(...keys) {
      for (const key of keys) {
        if (this.validationErrors[key]) return this.validationErrors[key]
      }
      return null
    },
    getOptionError(category) {
      // Match errors keyed like config_data.CategoryName.field
      const prefix = `config_data.${category}.`
      const msgs = []
      Object.entries(this.validationErrors).forEach(([key, msg]) => {
        if (key.startsWith(prefix) || key === `config_data.${category}`) {
          msgs.push(msg)
        }
      })
      return msgs.length ? msgs.join(', ') : null
    },
    async updatePriceData() {
      // Don't run if we don't have a valid substrate selection yet
      if (!this.selectedSubstrate) {
        this.priceData = null
        this.priceCommitted = false
        return
      }
      try {
        const payload = this.itemData
        if (!Object.keys(payload || {}).length) {
          this.priceData = null
          this.priceCommitted = false
          return
        }
        const itemId = this.$route.query.item
        const url = itemId
          ? `api/v3/design-studio/items/${itemId}/`
          : 'api/v3/design-studio/items/'

        this.pricingLoading = true

        const response = itemId
          ? await http2.patch(
              url,
              payload,
              { params: { dry_run: true, debug: this.isDebug } }
            )
          : await http2.post(
              url,
              payload,
              { params: { dry_run: true, debug: this.isDebug } }
            )

        this.validationErrors = {}

        const newPriceData = response.data && response.data.price_data ? response.data.price_data : null
        // If the dry-run price matches what we already have committed, keep the checkmark
        if (this.priceCommitted && this.priceData && newPriceData &&
            JSON.stringify(this.priceData) === JSON.stringify(newPriceData)) {
          this.priceData = newPriceData
        } else {
          this.priceData = newPriceData
          this.priceCommitted = false
        }
      } catch (e) {
        // On error, clear price so we don't show stale data
        this.priceData = null
        this.priceCommitted = false
        this.validationErrors = this.parseValidationErrors(e)
      } finally {
        this.pricingLoading = false
      }
    },
    async loadItem(itemId) {
      if (!itemId) return
      try {
        this.loadingItem = true
        if (!this.substrates.length) {
          await this.fetchSubstrates()
        }

        const response = await http2.get(`api/v3/design-studio/items/${itemId}/`)
        const item = response.data

        this.selectedImage = item.imagedata || null
        this.thumbnailUrl = item.thumbnail || getSrc(item.imagedata) || null
        this.quantity = Number(item.quantity) || 1
        this.location = item.location || ''
        this.dimensions = {
          width: String(item.config_data?.width ?? ''),
          height: String(item.config_data?.height ?? ''),
        }
        const cfg = item.config_data || {}
        const scale = cfg.artwork_scale != null ? cfg.artwork_scale : cfg.artworkScale
        const offsetLeft = cfg.artwork_offset_left != null ? cfg.artwork_offset_left : cfg.artworkOffsetLeft
        const offsetTop = cfg.artwork_offset_top != null ? cfg.artwork_offset_top : cfg.artworkOffsetTop
        const sVal = Number(scale) || 1
        this.artworkScale = Math.round(Math.max(1, sVal) * 100) / 100
        const oLeft = Number(offsetLeft) || 0
        const oTop = Number(offsetTop) || 0
        this.artworkOffsetLeft = Math.round(oLeft * 100) / 100
        this.artworkOffsetTop = Math.round(oTop * 100) / 100
        this.finishedDimensions = {
          width: Number(item.width) || null,
          height: Number(item.height) || null,
        }

        const substrateName = item.substrate || item.config_data?.substrate
        if (substrateName && this.substrates.length) {
          const sub = this.substrates.find(s => s.name?.toLowerCase() === String(substrateName).toLowerCase())
          this.selectedSubstrate = sub ? sub.id : null
          this.pendingChosenOptions = item.config_data?.chosen_options || null
        }

        const projectId = typeof item.project === 'object' ? item.project?.id : item.project
        if (projectId && item.workspace) {
          await this.loadWorkspaceForItem(projectId, item.workspace)
        }

        // If the item already has price_data, use it as the expected/committed price.
        if (item.price_data) {
          this.priceData = item.price_data
          this.priceCommitted = true
        } else {
          this.priceData = null
          this.priceCommitted = false
        }
      } catch (e) {
        console.error('Failed to load item', e)
        this.$bvToast.toast('Failed to load item', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loadingItem = false
      }
    },
    async applyQueryConfig(query) {
      const width = query?.width
      const height = query?.height
      const substrate = query?.substrate
      const quantity = query?.quantity
      const location = query?.location
      const workspaceId = query?.workspace_id

      if (width !== undefined && width !== null && width !== '') {
        this.dimensions.width = String(width)
      }
      if (height !== undefined && height !== null && height !== '') {
        this.dimensions.height = String(height)
      }

      if (substrate && this.substrates.length) {
        const substrateValue = String(substrate).trim().toLowerCase()
        const matched = this.substrates.find(s => String(s.id) === String(substrate))
          || this.substrates.find(s => String(s.name || '').trim().toLowerCase() === substrateValue)
        if (matched) {
          this.selectedSubstrate = matched.id
        }
      }

      const parsedQty = parseInt(quantity, 10)
      if (!isNaN(parsedQty) && parsedQty > 0) {
        this.quantity = parsedQty
      }
      if (location !== undefined && location !== null) {
        this.location = String(location)
      }

      const parsedWorkspaceId = parseInt(workspaceId, 10)
      if (!isNaN(parsedWorkspaceId) && parsedWorkspaceId > 0) {
        await this.loadWorkspaceById(parsedWorkspaceId)
      }
    },
    async loadImageFromQuery(imageId, query = this.$route.query) {
      if (!imageId) return
      try {
        this.loadingItem = true
        if (!this.substrates.length) {
          await this.fetchSubstrates()
        }
        const response = await http.get(`content/retrieve/${encodeURIComponent(imageId)}/`)
        const image = response?.data
        if (!image?.id) {
          throw new Error('Image not found')
        }
        this.selectedImage = image
        this.thumbnailUrl = getSrc(image) || null
        await this.applyQueryConfig(query)
        this.ensureSourceCropDefaults(true)
      } catch (e) {
        console.error('Failed to load image from query', e)
        this.$bvToast.toast('Failed to load source image', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loadingItem = false
      }
    },
    async loadWorkspaceForItem(projectId, workspaceName) {
      try {
        const response = await getAppApi().get('/api/v2/myworkspaces/', {
          params: { page_size: 100 },
        })
        const results = response.data?.results || []
        const pid = Number(projectId)
        const ws = results.find(w =>
          Number(w.project) === pid && (w.name === workspaceName || w.label?.includes(workspaceName))
        )
        if (ws) this.selectedWorkspace = ws
      } catch {
        // Ignore - user can re-select workspace
      }
    },
    async loadWorkspaceById(workspaceId) {
      try {
        const response = await getAppApi().get('/api/v2/myworkspaces/', {
          params: { page_size: 100 },
        })
        const results = response.data?.results || []
        const ws = results.find(w => Number(w.id) === Number(workspaceId))
        if (ws) {
          this.selectedWorkspace = ws
        }
      } catch {
        // Ignore - user can re-select workspace
      }
    },
    async fetchSubstrates() {
      this.loadingSubstrates = true
      try {
        this.substrates = await this.$store.dispatch('cache/fetch', 'substrates')
      } catch (e) {
        console.error('Failed to fetch substrates', e)
        this.$bvToast.toast('Failed to load substrates', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loadingSubstrates = false
      }
    },
    async fetchOptions(substrateId) {
      this.loadingOptions = true
      try {
        const response = await http2.get('api/v3/design-studio/available-options/', {
          params: {
            substrate: substrateId,
            limit: 10000
          }
        })
        this.availableOptions = Array.isArray(response.data) ? response.data : (response.data.results || [])

        // Initialize selectedOptions with defaults or null
        const optionsByCategory = groupBy(this.availableOptions, 'name')
        const initialSelection = {}
        Object.keys(optionsByCategory).forEach(category => {
          if (String(category || '').trim().toLowerCase() === 'misc') return
          const options = optionsByCategory[category]
          const defaultOpt = options.find(o => o.is_default)
          initialSelection[category] = defaultOpt ? defaultOpt.id : null
        })
        this.selectedOptions = initialSelection

      } catch (e) {
        console.error('Failed to fetch options', e)
        this.$bvToast.toast('Failed to load options', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
        this.availableOptions = []
      } finally {
        this.loadingOptions = false
      }
    },
    onFrameResize({ width, height }) {
      this.dimensions.width = String(width)
      this.dimensions.height = String(height)
    },
    onSourceTransform({ scale, offsetLeft, offsetTop }) {
      if (Number.isFinite(scale) && scale >= 1) {
        this.artworkScale = Math.round(scale * 100) / 100
      }
      if (Number.isFinite(offsetLeft)) {
        this.artworkOffsetLeft = Math.round(offsetLeft * 100) / 100
      }
      if (Number.isFinite(offsetTop)) {
        this.artworkOffsetTop = Math.round(offsetTop * 100) / 100
      }
    },
    onTransitionThumbnailReady() {
      const resolve = this.$store.state.appearance?.viewTransitionReadyResolve
      if (typeof resolve === 'function') {
        resolve()
      }
    },
    onRequestCameraMode(mode) {
      if (shouldPromoteCameraMode(this.cameraMode, mode)) {
        this.cameraMode = mode
      }
    },
    onEnterFreeLook() {
      this.isFreeLookActive = true
    },
    onExitFreeLook() {
      this.isFreeLookActive = false
    },
    onGlobalKeyDown(event) {
      const key = event.key.toLowerCase()
      if (!['w', 'a', 's', 'd'].includes(key)) return
      const target = event.target
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      if (this.isFreeLookActive) return

      if (this.cameraMode !== CAMERA_MODE_FREE) {
        this.cameraMode = CAMERA_MODE_FREE
        return
      }

      // If we're already in 'free' mode but pointer lock isn't active (e.g. after Esc),
      // re-enter free-look directly.
      const renderer = this.$refs.frameRenderer
      if (renderer && typeof renderer.enableFreeLook === 'function') {
        renderer.enableFreeLook()
      }
    },
    setCameraMode(mode) {
      const normalized = String(mode || '').trim().toLowerCase()
      if (normalized === CAMERA_MODE_FREE) {
        const renderer = this.$refs.frameRenderer
        if (this.cameraMode === CAMERA_MODE_FREE && renderer && typeof renderer.enableFreeLook === 'function') {
          renderer.enableFreeLook()
          return
        }
      }
      if (normalized === 'standard' || normalized === 'wall' || normalized === CAMERA_MODE_FREE) {
        this.cameraMode = normalized
      }
    },
    toggleFrameFlip() {
      const renderer = this.$refs.frameRenderer
      if (renderer && typeof renderer.toggleFlip === 'function') {
        renderer.toggleFlip()
      }
    },
    downloadRender() {
      const renderer = this.$refs.frameRenderer
      if (renderer) {
        const title = this.selectedImage?.title || 'frame-render'
        const safeName = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50)
        renderer.downloadImage(`${safeName}.png`)
      }
    },
    async saveDesign() {
      this.saving = true
      try {
        const payload = this.itemData

        // Capture thumbnail and add to payload
        const renderer = this.$refs.frameRenderer
        if (renderer && typeof renderer.exportThumbnail === 'function') {
          const thumbDataUrl = renderer.exportThumbnail(800)
          if (thumbDataUrl) {
            payload.thumbnail = thumbDataUrl
          }
        }

        const expectedPrice = this.priceData
        // Using http2 for main API
        const itemId = this.$route.query.item
        const url = itemId
          ? `api/v3/design-studio/items/${itemId}/`
          : 'api/v3/design-studio/items/'

        const response = itemId
          ? await http2.patch(
              url,
              payload,
              { params: { dry_run: false, debug: this.isDebug } }
            )
          : await http2.post(
              url,
              payload,
              { params: { dry_run: false, debug: this.isDebug } }
            )

        const savedThumbnail = response?.data?.thumbnail
        if (savedThumbnail) {
          this.thumbnailUrl = savedThumbnail
          this.finishedDimensions = {
            width: Number(response.data.width),
            height: Number(response.data.height),
          }
        }

        // Update local priceData from saved item, if present
        const savedPrice = response && response.data && response.data.price_data
        if (savedPrice) {
          this.priceData = savedPrice
        }

        // Assert that saved price matches last dry run price, if both exist
        if (expectedPrice && savedPrice) {
          const expectedJson = JSON.stringify(expectedPrice)
          const savedJson = JSON.stringify(savedPrice)
          if (expectedJson !== savedJson) {
            console.error('Design Studio price mismatch between dry run and saved item', {
              expected: expectedPrice,
              saved: savedPrice,
            })
            this.$bvToast.toast('Warning: saved price does not match previewed price.', {
              title: 'Pricing Mismatch',
              variant: 'warning',
              solid: true,
            })
            this.priceCommitted = false
          } else {
            this.priceCommitted = true
          }
        } else if (savedPrice) {
          // We only have a saved price, treat that as committed
          this.priceCommitted = true
        }

        // If we just created a new item, redirect to URL with its id
        if (!itemId && response && response.data && response.data.id) {
          this.$router.replace({
            path: '/design-studio',
            query: { item: response.data.id },
          })
        }

      } catch (e) {
        console.error('Failed to save design', e)
        this.$bvToast.toast('Failed to save design', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.ds-page {
  font-size: 0.85rem;
}

.ds-sidebar {
  padding-left: 1rem;
}

.ds-debug-toggle {
  font-size: 0.75rem;
  color: #6c757d;
}

</style>
